/**
 * Detects the upstream-error strings the AI Engine / agent forwards as if they
 * were an Anderra reply ("Error processing your question: AI Engine error:
 * 503", "Error processing your question: OpenAI API quota exhausted — circuit
 * breaker active…"). They must never render or persist as an answer.
 *
 * The LiveKit text-stream handler (#303) already intercepts them, but the
 * agent ALSO persists its replies to the DB, and the two DB→Redux hydration
 * paths (useChatSync, useSupabaseRealtime) rehydrated the error as history —
 * pinnable, surviving reload (extreme module 05 F10, staging 2026-08-17).
 * One detector, used at every boundary.
 */
export function isUpstreamChatErrorText(text: string | null | undefined): boolean {
  if (!text) return false;
  const lower = text.toLowerCase();
  return (
    lower.startsWith('error processing your question') ||
    lower.includes('ai engine error:') ||
    (/\b5\d{2}\b/.test(lower) && lower.includes('error'))
  );
}

/** Agent/Anderra authorship, mirroring useOfficerChatHandler's heuristic. */
export function isAgentIdentity(identity: string | null | undefined): boolean {
  if (!identity) return false;
  return identity.startsWith('agent-') || identity.includes('agent');
}

/** Drop agent-authored upstream-error rows from a hydrated officer-chat list. */
export function dropUpstreamErrorMessages<T extends { message?: string; from?: { identity?: string } }>(messages: T[]): T[] {
  return messages.filter((m) => !(isAgentIdentity(m.from?.identity) && isUpstreamChatErrorText(m.message)));
}
